import {createSlice} from '@reduxjs/toolkit';
import moment from 'moment';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
// eslint-disable-next-line import/no-cycle
import {getErrorMessage} from '@utils/requests/error';
import {transactionApi} from '@api/transaction/api';
import {recipientApi} from '@api/recipient/api';
import {documentVisibilityApi} from '@api/documentVisibility/api';
import {auditApi} from '@api/audit/api';
import {reminderApi} from '@api/reminder/api';
import {documentApi} from '@api/document/api';
import {getApprovalsForTransactionByDocumentId} from '@components/Packages/utils/getApprovalsAndFields';
import {bulkApi} from '@api/bulk/api';
import {configurationApi} from '@api/configuration/api';
import {CONFIGURATION_TYPES} from '@api/constants';
import {fetchAvatarsByEmails} from '@components/Packages/Recipients/recipientsSlice';
import {getIsSenderRecipient} from '@components/Packages/Recipients/utils';
import {
    BASE_PATH,
    PACKAGE_DIR_OPTIONS,
    PACKAGES_PER_PAGE_OPTIONS,
    PACKAGE_TYPES,
    PACKAGE_QUERY_OPTIONS,
    PACKAGE_SORT_OPTIONS,
    PACKAGE_PREDEFINED_OPTIONS,
    DEFAULT_TEMPLATES_OPTIONS,
    FETCH_OPTIONS_STEP,
    LAST_TRANSACTION_COUNT,
    VISIBILITY_ACCOUNT,
    VISIBILITY_SENDER
} from '@constants';
import httpClient from '@utils/httpClient';
import {fetchTransactionsSummary} from '@components/Transactions/transactionsSlice';
import {selectIsLast30DaysFilter} from '@components/appSlice';
import {selectActiveAccountId} from '@components/Login/userSlice';
import {selectCurrentUserEmails} from '@utils/currentUserEmails';
import {getOwnerRecipientToCreate} from '@utils/getOwnerRecipientToCreate';

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: {
        overview: {},
        lastTransactions: [],
        signerTransactions: [],
        visibility: {},
        auditEvents: {},
        approvalsByTransactionId: {},
        fetchingApprovalsByTransactionId: {},
        templates: [],
        templatesCount: null,
        allTemplates: [],
        uploadErrors: [],
        remindersInfo: {},
        reminders: {},
        verifications: {},
        isOverviewFetching: false,
        fetchingOverviewError: null,
        isSavingDataAttr: false,
        savingDashboardTypeError: null,
        isLastTransactionsFetching: false,
        fetchingLastTransactionsError: null,
        isSignerTransactionsFetching: false,
        fetchingSignerTransactionsError: null,
        selectedTransactionId: null,
        isVisibilityFetching: false,
        fetchingVisibilityError: null,
        isTemplatesFetching: false,
        fetchingTemplatesError: null,
        isCreatingTransaction: false,
        creatingTransactionError: null,
        isAuditEventsFetching: false,
        isRemindersFetching: false,
        packageSettings: {}
    },
    reducers: {
        setOverview: (state, action) => {
            state.overview = action.payload;
        },
        setLastTransactions: (state, action) => {
            state.lastTransactions = action.payload;
        },
        setSignerTransactions: (state, action) => {
            state.signerTransactions = action.payload;
        },
        setVisibility: (state, action) => {
            state.visibility = action.payload;
        },
        setAuditEvents: (state, action) => {
            state.auditEvents = {
                ...state.auditEvents,
                ...action.payload
            };
        },
        setIsOverviewFetching: (state, action) => {
            state.isOverviewFetching = action.payload;
        },
        setFetchingOverviewError: (state, action) => {
            state.fetchingOverviewError = action.payload;
        },
        setIsSavingDataAttr: (state, action) => {
            state.isSavingDataAttr = action.payload;
        },
        setSavingDashboardTypeError: (state, action) => {
            state.savingDashboardTypeError = action.payload;
        },
        setIsLastTransactionsFetching: (state, action) => {
            state.isLastTransactionsFetching = action.payload;
        },
        setFetchingLastTransactionsError: (state, action) => {
            state.fetchingLastTransactionsError = action.payload;
        },
        setIsSignerTransactionsFetching: (state, action) => {
            state.isSignerTransactionsFetching = action.payload;
        },
        setFetchingSignerTransactionsError: (state, action) => {
            state.fetchingSignerTransactionsError = action.payload;
        },
        setSelectedTransactionId: (state, action) => {
            state.selectedTransactionId = action.payload;
        },
        setIsVisibilityFetching: (state, action) => {
            state.isVisibilityFetching = action.payload;
        },
        setFetchingVisibilityError: (state, action) => {
            state.fetchingVisibilityError = action.payload;
        },
        setTemplates: (state, action) => {
            const {payload} = action;
            let updatedPayload = [...state.templates];
            if (payload.reset) {
                updatedPayload = [];
            } else {
                updatedPayload.splice(payload.from - 1, payload.data.length, ...payload.data);
            }
            state.templates = updatedPayload;
        },
        setTemplatesCount: (state, action) => {
            state.templatesCount = action.payload;
        },
        setAllTemplates: (state, action) => {
            state.allTemplates = action.payload;
        },
        setIsTemplatesFetching: (state, action) => {
            state.isTemplatesFetching = action.payload;
        },
        setFetchingTemplatesError: (state, action) => {
            state.fetchingTemplatesError = action.payload;
        },
        setIsCreatingTransaction: (state, action) => {
            state.isCreatingTransaction = action.payload;
        },
        setCreatingTransactionError: (state, action) => {
            state.creatingTransactionError = action.payload;
        },
        setUploadErrors: (state, action) => {
            state.uploadErrors = action.payload;
        },
        setIsAuditEventsFetching: (state, action) => {
            state.isAuditEventsFetching = action.payload;
        },
        setRemindersInfo: (state, action) => {
            const {
                packageId,
                roleId
            } = action.payload;
            const transactionState = get(state, `remindersInfo[${packageId}]`, {});
            const roleState = get(state, `remindersInfo[${packageId}][${roleId}]`, {});

            state.remindersInfo = {
                ...state.remindersInfo,
                [packageId]: {
                    ...transactionState,
                    [roleId]: {
                        ...roleState,
                        ...action.payload
                    }
                }
            };
        },

        setIsRemindersFetching: (state, action) => {
            state.isRemindersFetching = action.payload;
        },
        setReminders: (state, action) => {
            const {
                packageId,
                reminders
            } = action.payload;
            state.reminders = {
                ...state.reminders,
                [packageId]: reminders
            };
        },
        setPackageSettings: (state, action) => {
            state.packageSettings = action.payload;
        },
        updateTransaction: (state, action) => {
            const {transactionId, recipients, documents} = action.payload;
            const index = state.lastTransactions.findIndex(({id}) => id === transactionId);
            if (index !== -1) {
                state.lastTransactions[index] = {
                    ...state.lastTransactions[index],
                    recipients,
                    documents
                };
            }
        },
        setApprovalsForTransaction: (state, action) => {
            const {transactionId, documentApprovals} = action.payload;
            state.approvalsByTransactionId = {
                ...state.approvalsByTransactionId,
                [transactionId]: {
                    ...(state.approvalsByTransactionId[transactionId] || {}),
                    ...documentApprovals
                }
            };
        },
        setFetchingApprovalsForTransaction: (state, action) => {
            const {transactionId, isFetching} = action.payload;
            state.fetchingApprovalsByTransactionId = {
                ...state.fetchingApprovalsByTransactionId,
                [transactionId]: isFetching
            };
        },
        updateLastTransactionDocuments: (state, action) => {
            const {transactionId, documents} = action.payload;
            const index = state.lastTransactions.findIndex(({id}) => id === transactionId);
            if (index !== -1) {
                state.lastTransactions[index] = {
                    ...state.lastTransactions[index],
                    documents
                };
            }
        }
    }
});

export const {
    setOverview,
    setLastTransactions,
    setVisibility,
    setAuditEvents,
    setIsOverviewFetching,
    setFetchingOverviewError,
    setIsSavingDataAttr,
    setSavingDashboardTypeError,
    setIsLastTransactionsFetching,
    setFetchingLastTransactionsError,
    setSelectedTransactionId,
    setIsVisibilityFetching,
    setFetchingVisibilityError,
    setTemplates,
    setTemplatesCount,
    setIsTemplatesFetching,
    setFetchingTemplatesError,
    setIsCreatingTransaction,
    setCreatingTransactionError,
    setUploadErrors,
    setSignerTransactions,
    setIsSignerTransactionsFetching,
    setFetchingSignerTransactionsError,
    setIsAuditEventsFetching,
    setRemindersInfo,
    setReminders,
    setIsRemindersFetching,
    setPackageSettings,
    setAllTemplates,
    updateTransaction,
    setApprovalsForTransaction,
    setFetchingApprovalsForTransaction,
    updateLastTransactionDocuments
} = dashboardSlice.actions;

export const fetchOverview = ({
    headers, options = {}
} = {}) => async (dispatch, getState) => {
    const isLast30DaysFilter = selectIsLast30DaysFilter(getState());
    const apiOptions = {params: {days: isLast30DaysFilter ? 30 : 0}};

    if (headers) {
        apiOptions.headers = headers;
    }

    if (options.timeout) {
        apiOptions.timeout = options.timeout;
    }

    dispatch(setFetchingOverviewError(null));
    dispatch(setIsOverviewFetching(true));

    try {
        const result = await transactionApi.getTransactionsOverview(apiOptions);

        dispatch(setIsOverviewFetching(false));
        dispatch(setOverview(get(result, 'data', {})));
        return true;
    } catch (err) {
        dispatch(setIsOverviewFetching(false));
        dispatch(setFetchingOverviewError(getErrorMessage(err)));
        return false;
    }
};

export const fetchLastTransactions = ({
    headers, baseUrl, options = {}
} = {}) => async (dispatch, getState) => {
    dispatch(setIsLastTransactionsFetching(true));
    dispatch(setFetchingLastTransactionsError(null));

    try {
        const {user: userState} = getState();
        const accountId = selectActiveAccountId(getState()) || get(userState, 'account.id');
        const apiOptions = {};

        if (headers) {
            apiOptions.headers = headers;
        }

        if (options.timeout) {
            apiOptions.timeout = options.timeout;
        }

        const result = await transactionApi.getTransactions({
            accountId,
            params: {
                from: 1,
                to: LAST_TRANSACTION_COUNT,
                sort: '-updated_at',
                status: ['sent', 'completed', 'declined']
            },
            ...apiOptions
        });

        const transactions = get(result, 'data', []);
        const currentUser = get(userState, 'user', {});

        const getCurrentUserSender = (transaction) => (
            transaction.owner_id === currentUser.id
                ? {
                    id: currentUser.id,
                    email: currentUser.email,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    company: currentUser.organisation,
                    title: currentUser.title,
                    user_id: currentUser.id
                }
                : null
        );

        const getSenderFromRecipients = (recipients, ownerId) => {
            const ownerRecipient = (recipients || []).find((r) => getIsSenderRecipient(r, ownerId));
            if (!ownerRecipient) return null;
            return {
                id: ownerRecipient.id,
                email: ownerRecipient.email,
                firstName: ownerRecipient.first_name,
                lastName: ownerRecipient.last_name,
                company: ownerRecipient.organisation,
                title: ownerRecipient.title,
                user_id: ownerRecipient.user_id
            };
        };

        const baseLastTransactions = transactions.map((transaction) => ({
            ...transaction,
            sender: getCurrentUserSender(transaction),
            recipients: [],
            documents: []
        }));

        dispatch(setLastTransactions(baseLastTransactions));
        dispatch(setFetchingLastTransactionsError(null));
        dispatch(setIsLastTransactionsFetching(false));

        const recipientResults = await Promise.all(
            transactions.map((transaction) => recipientApi.getAllRecipients(
                transaction.id,
                {},
                apiOptions
            ).catch(() => []))
        );

        const lastTransactions = transactions.map((transaction, index) => {
            const recipients = recipientResults[index] || [];
            return {
                ...transaction,
                sender: getSenderFromRecipients(recipients, transaction.owner_id)
                    || getCurrentUserSender(transaction),
                recipients
            };
        });

        const recipientEmails = [...new Set(
            lastTransactions
                .flatMap((t) => (t.recipients || []).map((r) => r.email))
                .filter(Boolean)
        )];

        await dispatch(fetchAvatarsByEmails({
            baseUrl, headers, emails: recipientEmails, options
        }));

        dispatch(setLastTransactions(lastTransactions));

        return true;
    } catch (err) {
        dispatch(setIsLastTransactionsFetching(false));
        dispatch(setFetchingLastTransactionsError(getErrorMessage(err)));
        return false;
    }
};

export const fetchSingleTransaction = (transactionId) => async (dispatch) => {
    dispatch(setIsLastTransactionsFetching(true));

    try {
        const [recipients, docs] = await Promise.all([
            recipientApi.getAllRecipients(transactionId).catch(() => []),
            documentApi.getAllDocuments(transactionId).catch(() => [])
        ]);

        const documentApprovals = await getApprovalsForTransactionByDocumentId(
            transactionId,
            docs.map((doc) => doc.id)
        );

        dispatch(updateTransaction({transactionId, recipients, documents: docs}));
        dispatch(setApprovalsForTransaction({transactionId, documentApprovals}));
        dispatch(setIsLastTransactionsFetching(false));
        return true;
    } catch (err) {
        dispatch(setIsLastTransactionsFetching(false));
        return false;
    }
};

export const fetchLastTransactionDocumentsAndApprovals = (transactionId) => async (dispatch, getState) => {
    const {dashboard} = getState();
    const transaction = dashboard.lastTransactions.find(({id}) => id === transactionId);
    let docs = get(transaction, 'documents', []);
    const hasApprovalsInitial = !isEmpty(dashboard.approvalsByTransactionId?.[transactionId]);

    if (docs.length && hasApprovalsInitial) return true;

    dispatch(setFetchingApprovalsForTransaction({transactionId, isFetching: true}));
    try {
        if (!docs.length) {
            const docResult = await documentApi.getAllDocuments(transactionId).catch(() => []);
            docs = docResult || [];
            dispatch(updateLastTransactionDocuments({transactionId, documents: docs}));
        }

        if (!docs.length) return true;

        const documentApprovals = await getApprovalsForTransactionByDocumentId(
            transactionId,
            docs.map((doc) => doc.id)
        );

        dispatch(setApprovalsForTransaction({transactionId, documentApprovals}));
        return true;
    } catch {
        return false;
    } finally {
        dispatch(setFetchingApprovalsForTransaction({transactionId, isFetching: false}));
    }
};

export const fetchSignerTransactions = ({
    headers, baseUrl, options = {}
} = {}) => async (dispatch, getState) => {
    const apiOptions = {};

    if (headers) {
        apiOptions.headers = headers;
    }

    if (options.timeout) {
        apiOptions.timeout = options.timeout;
    }

    dispatch(setIsSignerTransactionsFetching(true));
    dispatch(setFetchingSignerTransactionsError(null));

    try {
        const accountId = selectActiveAccountId(getState()) || get(getState(), 'user.account.id');
        const result = await transactionApi.getTransactions({
            accountId,
            params: {
                per_page: Number(PACKAGES_PER_PAGE_OPTIONS[10]),
                page: 1,
                awaiting_signatures: 'true'
            },
            ...apiOptions
        });

        const transactions = get(result, 'data', []);
        const currentUser = get(getState(), 'user.user', {});

        const getSenderForTransaction = (transaction) => {
            const isCurrentUserOwner = transaction.owner_id === currentUser.id;
            return isCurrentUserOwner
                ? {
                    id: currentUser.id,
                    first_name: currentUser.firstName,
                    last_name: currentUser.lastName,
                    email: currentUser.email
                }
                : {
                    id: transaction.owner_id,
                    first_name: '',
                    last_name: '',
                    email: ''
                };
        };

        const sortByDue = (list) => [...list].sort((a, b) => {
            if (!a.expires_at) {
                return 1;
            }
            return moment(a.expires_at).valueOf() - moment(b.expires_at).valueOf();
        });

        const baseSignerTransactions = sortByDue(transactions.map((transaction) => ({
            ...transaction,
            sender: getSenderForTransaction(transaction),
            recipients: [],
            documents: []
        })));

        dispatch(setSignerTransactions(baseSignerTransactions));
        dispatch(setFetchingSignerTransactionsError(null));
        dispatch(setIsSignerTransactionsFetching(false));

        const [recipientResults, documentResults] = await Promise.all([
            Promise.all(
                transactions.map((transaction) => recipientApi.getAllRecipients(
                    transaction.id,
                    {},
                    apiOptions
                ).catch(() => []))
            ),
            Promise.all(
                transactions.map((transaction) => documentApi.getAllDocuments(
                    transaction.id,
                    {},
                    apiOptions
                ).catch(() => []))
            )
        ]);

        const enrichedTransactions = transactions.map((transaction, index) => {
            const recipients = recipientResults[index] || [];
            const documents = (documentResults[index] || []).map((doc) => ({
                ...doc,
                approvals: []
            }));
            return {
                ...transaction,
                sender: getSenderForTransaction(transaction),
                recipients,
                documents
            };
        });

        const sortedByDueTransactions = sortByDue(enrichedTransactions);

        const recipientEmails = [...new Set(
            sortedByDueTransactions
                .flatMap((t) => (t.recipients || []).map((r) => r.email))
                .filter(Boolean)
        )];
        const senderEmails = sortedByDueTransactions
            .map((t) => t.sender?.email)
            .filter(Boolean);

        await dispatch(fetchAvatarsByEmails({
            baseUrl,
            headers,
            emails: [...senderEmails, ...recipientEmails],
            options
        }));

        dispatch(setSignerTransactions(sortedByDueTransactions));

        return true;
    } catch (err) {
        dispatch(setIsSignerTransactionsFetching(false));
        dispatch(setFetchingSignerTransactionsError(getErrorMessage(err)));
        return false;
    }
};

export const fetchVisibility = (transactionId) => async (dispatch, getState) => {
    const {dashboard} = getState();

    dispatch(setIsVisibilityFetching(true));
    dispatch(setFetchingVisibilityError(null));

    try {
        const result = await documentVisibilityApi.getTransactionVisibility(transactionId);
        const configurations = get(result, 'configurations', []);

        dispatch(setVisibility({
            ...dashboard.visibility,
            [transactionId]: configurations
        }));
        dispatch(setIsVisibilityFetching(false));
        dispatch(setFetchingVisibilityError(null));
        return true;
    } catch (err) {
        dispatch(setIsVisibilityFetching(false));
        dispatch(setFetchingVisibilityError(getErrorMessage(err)));
        return false;
    }
};

export const fetchAudit = (transactionId) => async (dispatch) => {
    dispatch(setIsAuditEventsFetching(true));

    try {
        const result = await auditApi.getAudits(transactionId, {per_page: 100});
        const auditEvents = get(result, 'data', []);

        await dispatch(setAuditEvents({
            [transactionId]: auditEvents
        }));
        dispatch(setIsAuditEventsFetching(false));
        return true;
    } catch (err) {
        dispatch(setIsAuditEventsFetching(false));
        return false;
    }
};

export const fetchReminders = (packageId) => async (dispatch) => {
    dispatch(setIsRemindersFetching(true));

    try {
        const result = await reminderApi.getSchedules(packageId);
        const schedules = get(result, 'data', []);
        const reminders = schedules.flatMap((schedule) => schedule.reminders || []);
        dispatch(setIsRemindersFetching(false));
        dispatch(setReminders({
            packageId,
            reminders
        }));
        return true;
    } catch (err) {
        dispatch(setIsRemindersFetching(false));
        return false;
    }
};

export const sendReminder = (packageId, roleId) => async (dispatch) => {
    dispatch(setRemindersInfo({
        packageId,
        roleId,
        wasSaccessful: false,
        isSending: true,
        error: null
    }));

    try {
        const wasSuccessful = await reminderApi.sendNotification(packageId, roleId);
        dispatch(setRemindersInfo({
            packageId,
            roleId,
            wasSaccessful: !!wasSuccessful,
            isSending: false,
            error: null
        }));

        return true;
    } catch (err) {
        dispatch(setRemindersInfo({
            packageId,
            roleId,
            wasSaccessful: false,
            isSending: false,
            error: getErrorMessage(err)
        }));

        return false;
    }
};

export const saveDataAttr = (data) => async (dispatch, getState) => {
    const {user} = getState();
    const payload = {data};

    dispatch(setIsSavingDataAttr(true));
    dispatch(setSavingDashboardTypeError(null));

    try {
        await httpClient.post(`${BASE_PATH}/proxy/account/senders/${user.user.id}`, payload);
        const {setUser} = await import('../Login/userSlice');
        dispatch(setUser({
            ...user.user,
            data: {...(user.user.data || {}), ...data}
        }));
        dispatch(setIsSavingDataAttr(false));
        dispatch(setSavingDashboardTypeError(null));
        return true;
    } catch (err) {
        dispatch(setIsSavingDataAttr(false));
        dispatch(setSavingDashboardTypeError(getErrorMessage(err)));
        return false;
    }
};

export const fetchTemplates = ({
    headers, baseUrl, params = {}, options = {}
} = {}) => async (dispatch, getState) => {
    const {user: userState} = getState();
    const accountId = selectActiveAccountId(getState()) || get(userState, 'account.id');
    const from = params.from || 1;
    const to = params.to || FETCH_OPTIONS_STEP;
    const perPage = Math.max(to - from + 1, 1);
    const page = Math.max(Math.ceil(from / perPage), 1);

    const apiParams = {page, per_page: perPage};
    if (params.search) {
        apiParams.search = params.search;
    }
    if (params.visibility === VISIBILITY_ACCOUNT) {
        apiParams.shared = true;
    } else if (params.visibility === VISIBILITY_SENDER) {
        apiParams.shared = false;
    }

    const apiOptions = {};
    if (headers) apiOptions.headers = headers;
    if (options.timeout) apiOptions.timeout = options.timeout;
    if (baseUrl) apiOptions.baseUrl = baseUrl;

    dispatch(setIsTemplatesFetching(true));
    dispatch(setFetchingTemplatesError(null));

    try {
        const result = await transactionApi.getTemplates({accountId, params: apiParams, ...apiOptions});
        const resultTemplates = Array.isArray(result?.data) ? result.data : [];
        const totalCount = get(result, 'meta.total')
            ?? get(result, 'data.meta.total')
            ?? resultTemplates.length;

        if (!params.shouldSkipTemplatesSet) {
            dispatch(setTemplates({from, data: resultTemplates}));
            dispatch(setTemplatesCount(totalCount));
        }
        dispatch(setIsTemplatesFetching(false));
        dispatch(setFetchingTemplatesError(null));

        return resultTemplates;
    } catch (err) {
        dispatch(setIsTemplatesFetching(false));
        dispatch(setFetchingTemplatesError(getErrorMessage(err)));
        return false;
    }
};

function getTransactionRequestPayload({transaction, locale, timezoneId, packageSettings}) {
    const senderVisible = !!transaction?.data?.senderVisible;
    return {
        name: transaction?.name || '',
        language: transaction?.language || locale,
        timezone: transaction?.timezone || timezoneId,
        origin: transaction?.data?.origin || 'validsign',
        autocomplete: transaction?.autocomplete ?? true,
        expires_at: transaction?.expires_at ?? transaction?.due ?? null,
        message_to_recipients: transaction?.message_to_recipients || '',
        settings: {
            ...packageSettings,
            ...transaction?.settings,
            owner_is_recipient: String(senderVisible)
        }
    };
}

async function ensureOwnerRecipient({transactionId, currentUser, currentUserEmails}) {
    if (!transactionId || !currentUser?.email) {
        return;
    }
    try {
        const existing = await recipientApi.getAllRecipients(transactionId).catch(() => []);
        const data = getOwnerRecipientToCreate(existing, currentUser, currentUserEmails);
        if (!data) {
            return;
        }
        await recipientApi.createRecipient(transactionId, data);
    } catch (err) {
        // non-fatal: recipient creation failure should not break transaction creation
    }
}

async function uploadDocumentsBatch({transactionId, documents}) {
    if (isEmpty(documents) || !transactionId) {
        return [];
    }
    const formData = new FormData();
    const payloads = documents.map((doc, index) => ({
        name: doc.name,
        order: index
    }));
    formData.append('payloads', new Blob([JSON.stringify(payloads)], {type: 'application/json'}));
    documents.forEach((doc) => {
        formData.append('file', doc);
    });
    try {
        await documentApi.createDocuments(transactionId, formData);
        return [];
    } catch (err) {
        return [getErrorMessage(err)];
    }
}

export const createTransaction = ({
    transaction, template, documents, bulkCSV
}) => async (dispatch, getState) => {
    const {app, user, dashboard} = getState();
    const accountId = selectActiveAccountId(getState()) || get(user, 'account.id');
    const isTemplateBulkSendable = get(template, 'bulkSendable', false)
        && get(user.features, 'bulkSend', false)
        && !!bulkCSV;

    dispatch(setIsCreatingTransaction(true));
    dispatch(setCreatingTransactionError(null));

    try {
        let transactionId = null;

        if (isTemplateBulkSendable) {
            const bulkResult = await bulkApi.storeBulk({
                accountId,
                templateId: template.id,
                data: {csvFile: bulkCSV, name: transaction?.name}
            });
            transactionId = get(bulkResult, 'data.id', null);
        } else {
            const requestPayload = getTransactionRequestPayload({
                transaction,
                locale: app.locale,
                timezoneId: user.user.timezoneId,
                packageSettings: dashboard.packageSettings
            });

            let createdTransaction;
            if (!isEmpty(template)) {
                const cloneResult = await transactionApi.cloneTemplate({
                    accountId,
                    templateId: template.id,
                    data: {
                        name: requestPayload.name,
                        owner_email: user.user.email
                    }
                });
                createdTransaction = cloneResult.data;
                transactionId = createdTransaction?.id || null;
            } else {
                const createResult = await transactionApi.createTransaction({
                    accountId,
                    data: requestPayload
                });
                createdTransaction = createResult.data;
                transactionId = createdTransaction?.id || null;
                await ensureOwnerRecipient({
                    transactionId,
                    currentUser: user.user,
                    currentUserEmails: selectCurrentUserEmails(getState())
                });
            }

            const uploadErrors = await uploadDocumentsBatch({transactionId, documents});
            dispatch(setUploadErrors(uploadErrors));
        }

        dispatch(setIsCreatingTransaction(false));
        dispatch(setCreatingTransactionError(null));
        dispatch(fetchTransactionsSummary());

        return transactionId;
    } catch (err) {
        dispatch(setIsCreatingTransaction(false));
        if (!isTemplateBulkSendable) {
            dispatch(setCreatingTransactionError(getErrorMessage(err)));
        } else {
            dispatch(setCreatingTransactionError([getErrorMessage(err)]));
        }

        return false;
    }
};

export const fetchPackageSettings = ({
    headers
} = {}) => async (dispatch, getState) => {
    const accountId = selectActiveAccountId(getState()) || get(getState(), 'user.account.id');
    const apiOptions = headers ? {headers} : {};

    try {
        const result = await configurationApi.getConfiguration(
            accountId,
            CONFIGURATION_TYPES.TRANSACTION_SETTINGS,
            apiOptions
        );
        const settings = get(result, 'data.ceremony', get(result, 'data', {}));
        dispatch(setPackageSettings(settings));
        return true;
    } catch (err) {
        return false;
    }
};

export const selectOverview = (state) => state.dashboard.overview;
export const selectIsOverviewFetching = (state) => state.dashboard.isOverviewFetching;
export const selectFetchingOverviewError = (state) => state.dashboard.fetchingOverviewError;
export const selectIsSavingDataAttr = (state) => state.dashboard.isSavingDataAttr;
export const selectLastTransactions = (state) => state.dashboard.lastTransactions;
export const selectIsLastTransactionsFetching = (state) => state
    .dashboard.isLastTransactionsFetching;
export const selectFetchingLastTransactionsError = (state) => state
    .dashboard.fetchingLastTransactionsError;
export const selectVisibility = (state) => state.dashboard.visibility;
export const selectAuditEvents = (state) => state.dashboard.auditEvents;
export const selectApprovalsByTransactionId = (transactionId) => (state) => (
    state.dashboard.approvalsByTransactionId[transactionId] || {}
);
export const selectIsFetchingApprovalsForTransaction = (transactionId) => (state) => (
    !!state.dashboard.fetchingApprovalsByTransactionId[transactionId]
);
export const selectSelectedTransactionId = (state) => state.dashboard.selectedTransactionId;
export const selectIsVisibilityFetching = (state) => state.dashboard.isVisibilityFetching;
export const selectTemplates = (state) => state.dashboard.templates;
export const selectTemplatesCount = (state) => state.dashboard.templatesCount;
export const selectAllTemplates = (state) => state.dashboard.allTemplates;
export const selectIsTemplatesFetching = (state) => state.dashboard.isTemplatesFetching;
export const selectIsCreatingTransaction = (state) => state.dashboard.isCreatingTransaction;
export const selectCreatingTransactionError = (state) => state.dashboard.creatingTransactionError;
export const selectUploadErrors = (state) => state.dashboard.uploadErrors;
export const selectThreeSignerTransactions = (state) => state.dashboard.signerTransactions?.slice(0, 3);
export const selectSignerTransactionsByAmount = (amount) => (state) => state.dashboard.signerTransactions?.slice(0, amount);
export const selectIsSignerTransactionsFetching = (state) => state
    .dashboard.isSignerTransactionsFetching;
export const selectFetchingSignerTransactionsError = (state) => state
    .dashboard.fetchingSignerTransactionsError;
export const selectIsAuditEventsFetching = (state) => state.dashboard.isAuditEventsFetching;
export const selectRemindersInfo = (state) => state.dashboard.remindersInfo;
export const selectReminders = (state) => state.dashboard.reminders;
export const selectIsRemindersFetching = (state) => state.dashboard.isRemindersFetching;
export const selectPackageSettings = (state) => state.dashboard.packageSettings;

export default dashboardSlice.reducer;
